import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Moon, Sun } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { visibleWorkspaces } from './settingsNavConfig';
import { isAdminRole } from '../../lib/roles';

// Matches Tailwind's lg, where SettingsLayout starts drawing its sidebar. Below
// it (phones and tablets) this hub is the way into a workspace.
const DESKTOP_BREAKPOINT = 1024;

export default function SettingsIndexPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { hasVertical, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const userIsAdmin = isAdminRole(user?.role);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);

  // The QuickBooks OAuth callback redirects to /settings?qbo=connected|error, not
  // to the Integrations page that reads the param. Forward it there with the query
  // intact — otherwise the desktop redirect below drops it and the OAuth outcome is
  // never shown to anyone. Runs before that redirect so it wins on both breakpoints.
  const qboReturn = new URLSearchParams(search).has('qbo');
  useEffect(() => {
    if (qboReturn) {
      navigate({ pathname: '/settings/integrations', search }, { replace: true });
    }
  }, [qboReturn, search, navigate]);

  // Redirect to Company Profile on desktop (sidebar handles nav there).
  useEffect(() => {
    if (isDesktop && !qboReturn) {
      navigate('/settings/company-profile', { replace: true });
    }
  }, [isDesktop, qboReturn, navigate]);

  // Keep responsive — if user resizes into desktop range, redirect.
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // This component redirects on desktop, and on any breakpoint for an OAuth
  // return; render nothing while the redirect fires.
  if (isDesktop || qboReturn) return null;

  const workspaces = visibleWorkspaces({ hasVertical, isAdmin: userIsAdmin });

  return (
    /* NO HEADING AND NO SURFACE OF ITS OWN. The band on SettingsLayout titles
       the whole surface on every breakpoint, so a second "Settings" h1 here
       would repeat it directly underneath; and the page fill this used to set
       is the page colour, which inside the layout's card panel would paint a
       grey rectangle through the middle of a white card in light mode. */
    <div className="min-h-full">
      <div className="px-6 py-4 pb-32 space-y-2 max-w-2xl">
        {workspaces.map((ws) => (
          <button
            key={ws.key}
            onClick={() => navigate(ws.tabs[0].route)}
            className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-accent transition-colors"
          >
            <div className="p-2 rounded-md bg-muted">
              <ws.icon className="h-5 w-5 text-foreground" />
            </div>
            <span className="flex-1 text-left text-sm font-medium text-foreground">
              {ws.label}
            </span>
            <ChevronRight className="h-4 w-4 text-fieldiq-muted" />
          </button>
        ))}
        {/* The phone header has no theme toggle (it gave its width to the company
          * name); this row is where it went. This page only renders below md. */}
        <button
          onClick={toggleTheme}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-accent transition-colors"
        >
          <div className="p-2 rounded-md bg-muted">
            {isDark ? <Moon className="h-5 w-5 text-foreground" /> : <Sun className="h-5 w-5 text-foreground" />}
          </div>
          <span className="flex-1 text-left text-sm font-medium text-foreground">Theme</span>
          <span className="text-sm text-fieldiq-muted">{isDark ? 'Dark' : 'Light'}</span>
        </button>
      </div>
    </div>
  );
}
